import { useUserStore } from "./userStore";

/**
 * Checks if the application audio should be played based on user preferences.
 * @returns true if audio is enabled, false if it is muted.
 */
export const isAppAudioEnabled = (): boolean => {
  const { user_attributes } = useUserStore.getState();
  // isEnableAudio: true means MUTE is ON (per user requirement)
  const isMuteActive = user_attributes["isEnableAudio"] === true || user_attributes["isEnableAudio"] === "true";
  return !isMuteActive;
};

let globalAudioContext: AudioContext | null = null;
const audioBufferCache = new Map<string, AudioBuffer>();
let isAudioContextUnlocked = false;

/**
 * Lazily initializes and returns the singleton Web AudioContext.
 */
export const getAudioContext = (): AudioContext | null => {
  if (typeof window === "undefined") return null;
  if (!globalAudioContext) {
    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (AudioCtx) {
      globalAudioContext = new AudioCtx();
    }
  }
  return globalAudioContext;
};

/**
 * Preloads and decodes an audio file into an in-memory AudioBuffer for instant, non-blocking playback.
 */
export const preloadAudio = async (url: string): Promise<AudioBuffer | null> => {
  const ctx = getAudioContext();
  if (!ctx) return null;

  if (audioBufferCache.has(url)) {
    return audioBufferCache.get(url)!;
  }

  try {
    const res = await fetch(url);
    const arrayBuffer = await res.arrayBuffer();
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
    audioBufferCache.set(url, audioBuffer);
    return audioBuffer;
  } catch (err) {
    console.warn(`[audioManager] Failed to preload/decode audio from ${url}:`, err);
    return null;
  }
};

/**
 * Unlocks browser audio playback using a physical user gesture.
 * Modern mobile browsers (Android Chrome, Samsung Internet, iOS Safari) require
 * AudioContext.resume() to be invoked within a user gesture to permit programmatic background playback.
 */
export const unlockAudioContext = () => {
  const ctx = getAudioContext();
  if (!ctx) return;

  if (ctx.state === "suspended") {
    ctx
      .resume()
      .then(() => {
        isAudioContextUnlocked = true;
        console.log("[audioManager] Web AudioContext successfully resumed by user gesture.");
      })
      .catch((err) => {
        console.warn("[audioManager] Failed to resume AudioContext:", err);
      });
  } else if (ctx.state === "running") {
    isAudioContextUnlocked = true;
  }

  // Play a silent 1-sample buffer to warm up audio hardware on mobile
  try {
    const buffer = ctx.createBuffer(1, 1, 22050);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);
  } catch {
    // Ignore warmup error
  }
};

// Auto-register one-time global user gesture listener on the window
if (typeof window !== "undefined") {
  const handleFirstUserGesture = () => {
    unlockAudioContext();
    window.removeEventListener("pointerdown", handleFirstUserGesture, true);
    window.removeEventListener("touchstart", handleFirstUserGesture, true);
    window.removeEventListener("click", handleFirstUserGesture, true);
    window.removeEventListener("keydown", handleFirstUserGesture, true);
  };

  window.addEventListener("pointerdown", handleFirstUserGesture, true);
  window.addEventListener("touchstart", handleFirstUserGesture, true);
  window.addEventListener("click", handleFirstUserGesture, true);
  window.addEventListener("keydown", handleFirstUserGesture, true);

  // Resume on tab visibility change if previously unlocked
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      const ctx = getAudioContext();
      if (ctx && ctx.state === "suspended" && isAudioContextUnlocked) {
        ctx.resume().catch(() => {});
      }
    }
  });
}

/**
 * Plays an audio file if the user has not muted notifications.
 * Prioritizes the Web Audio API for mobile compatibility (preventing Autoplay policy blocks),
 * with graceful fallback to standard HTMLAudioElement.
 * @param src Path to the audio file or an Audio object.
 * @returns The Audio object if played via HTMLAudioElement, or null.
 */
export const playAppAudio = (src: string | HTMLAudioElement): HTMLAudioElement | null => {
  if (!isAppAudioEnabled()) {
    return null;
  }

  // If src is a string URL, attempt playback via Web Audio API
  if (typeof src === "string") {
    const ctx = getAudioContext();
    if (ctx) {
      if (ctx.state === "suspended") {
        ctx.resume().catch(() => {});
      }

      const cachedBuffer = audioBufferCache.get(src);
      if (cachedBuffer) {
        try {
          const source = ctx.createBufferSource();
          source.buffer = cachedBuffer;
          source.connect(ctx.destination);
          source.start(0);
          return null;
        } catch (err) {
          console.warn("[audioManager] WebAudio buffer playback error:", err);
        }
      } else {
        // Asynchronously decode and play, or fallback if decoding fails
        preloadAudio(src)
          .then((buffer) => {
            if (buffer && ctx.state === "running") {
              try {
                const source = ctx.createBufferSource();
                source.buffer = buffer;
                source.connect(ctx.destination);
                source.start(0);
                return;
              } catch (err) {
                console.warn("[audioManager] WebAudio buffer playback error:", err);
              }
            }
            // Fallback if not running or buffer failed
            const fallbackAudio = new Audio(src);
            fallbackAudio.play().catch((err) => {
              console.warn("[audioManager] Fallback audio playback blocked:", err);
            });
          })
          .catch(() => {
            const fallbackAudio = new Audio(src);
            fallbackAudio.play().catch((err) => {
              console.warn("[audioManager] Fallback audio playback blocked:", err);
            });
          });
        return null;
      }
    }
  }

  // Fallback to HTMLAudioElement
  const audio = typeof src === "string" ? new Audio(src) : src;
  audio.play().catch((err) => {
    console.warn("[audioManager] Audio playback failed or was blocked by browser:", err);
  });
  return audio;
};
